/**
 * Actionable Follow-Up Questions parsing utilities.
 *
 * The backend appends a follow-up section to the end of an assistant answer using a
 * single section marker followed by prose with inline `<followup>…</followup>` tagged
 * questions interspersed:
 *
 *   Normal answer text...
 *
 *   ◈If you want I can <followup>Convert this to a FAQ</followup>, <followup>Add target personas</followup>, or <followup>Make the tone more formal</followup>
 *
 * There is no end marker — everything after the first `◈` is the follow-up payload and
 * is assumed to be the last thing in the message. The surrounding prose renders as
 * normal text, while each `<followup>…</followup>` renders as an inline clickable link.
 *
 * See LDR: https://hello.atlassian.net/wiki/spaces/AA6/pages/7164582669
 */

/** Section marker (U+25C8) that denotes the start of the follow-up questions payload. */
export const ACTIONABLE_FOLLOW_UPS_MARKER = '◈';

/** Status-node localId prefix used to render the follow-ups component inline. */
export const ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX = 'actionable-follow-ups-';

/** Whether a status node localId belongs to an actionable follow-ups component. */
export const isActionableFollowUpsStatus = (localId?: string): boolean => {
	return localId != null && localId.startsWith(ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX);
};

/**
 * Matches each `<followup>…</followup>` segment in the follow-up payload. The inner capture
 * is non-greedy and allows any character (including `<`/`>`), so question text containing
 * angle brackets parses correctly. An unterminated `<followup>` with no closing tag simply
 * does not match and is treated as in-flight prose.
 */
const FOLLOW_UP_QUESTION_REGEX = /<followup>([\s\S]*?)<\/followup>/g;

/** A run of surrounding prose that renders as normal text. */
export type FollowUpTextSegment = { type: 'text'; value: string };

/** A single follow-up question that renders as an inline clickable link. */
export type FollowUpQuestionSegment = { type: 'question'; value: string };

/**
 * An ordered piece of the follow-up section: either surrounding prose (`text`) or a
 * clickable question (`question`). Rendering the segments in order reproduces the
 * original sentence with each `<followup>` swapped for an inline link.
 */
export type FollowUpSegment = FollowUpTextSegment | FollowUpQuestionSegment;

/**
 * Parses the raw follow-up payload (the text that follows the `◈` marker) into an ordered
 * list of segments: prose `text` segments and clickable `question` segments.
 *
 * The surrounding prose is preserved verbatim (only the `<followup>`/`</followup>` tags are
 * removed) so it can be rendered as normal stream text, with each question rendered inline.
 *
 * Malformed input is handled gracefully: empty/whitespace-only questions are dropped, and an
 * unterminated trailing `<followup>` fragment (an incomplete stream) is omitted entirely so a
 * half-streamed question never flashes as raw text.
 *
 * @param payload the text after the `◈` marker (may include the marker itself).
 */
export const parseActionableFollowUps = (payload: string): FollowUpSegment[] => {
	if (!payload) {
		return [];
	}

	// Strip a leading marker if the caller passed the full section including `◈`.
	const markerIndex = payload.indexOf(ACTIONABLE_FOLLOW_UPS_MARKER);
	let text =
		markerIndex === -1 ? payload : payload.slice(markerIndex + ACTIONABLE_FOLLOW_UPS_MARKER.length);

	// Drop an unterminated trailing `<followup>` fragment so a half-streamed question
	// is not rendered as raw text until its closing tag arrives.
	const lastOpen = text.lastIndexOf('<followup>');
	if (lastOpen !== -1 && text.indexOf('</followup>', lastOpen) === -1) {
		text = text.slice(0, lastOpen);
	}

	const segments: FollowUpSegment[] = [];
	let lastIndex = 0;

	const pushText = (value: string) => {
		if (value) {
			segments.push({ type: 'text', value });
		}
	};

	for (const match of text.matchAll(FOLLOW_UP_QUESTION_REGEX)) {
		const matchIndex = match.index ?? 0;
		// Prose preceding this question (preserved verbatim).
		pushText(text.slice(lastIndex, matchIndex));
		lastIndex = matchIndex + match[0].length;

		const question = match[1]?.trim();
		if (question) {
			segments.push({ type: 'question', value: question });
		}
	}

	// Trailing prose after the final question.
	pushText(text.slice(lastIndex));

	return segments;
};
